import React, { useState, useEffect } from "react";
import { Button, Col,  Modal, ModalBody, ModalHeader, Row } from "reactstrap";
import { RailDocument } from "shared/models/api/models";
import { Document } from "shared/models/site/document";
import { RelatedDocument } from "shared/models/site/related-document";
import { ExitCancelModel } from 'components/ui/InfoModal';
import { IDocumentFilter, RelatedRulebookDocumentViewTraditional, RelatedRulebookDocumentViewCBTC, IDocumentModule } from "components/ui/DocumentRules";
import { IListItemProps, List } from "components/ui/Lists";
import { Checkbox } from "components/ui/LabelledCheckbox";
import { DocumentFilterList, ILeafItem, ModalClose, RelatedDocumentsH2 } from "components/menu/Labels";
import { documentAddEditState } from './AddEditDocumentState'
import { Chip } from "@mui/material";
import './RelatedDocuments.css'
import { getUserExistingRulebookMode } from "../../../../shared/store/applicationState";
import { RulebookModeEnum, RulebookNameToEnum } from "../../../../shared/enums/rulebook-mode-enum";


interface IRelatedDocumentChipsProps {
    documents: Array<RelatedDocument>,
    deleteAction?: (id: number) => void
}

export const RelatedDocumentChips = (props: IRelatedDocumentChipsProps) => {
    return (
        <div>
            {
                props.documents.map((d, i) => {
                    const deleteAction = props.deleteAction ? () => {
                        if (props.deleteAction) props.deleteAction(d.id)
                    } : undefined;

                    return (
                        <Chip key={i} className="m-1" label={d.title} onDelete={deleteAction} />
                    )
                })
            }
        </div>
    );
}

export const RelatedDocumentList = (props: IRelatedDocumentChipsProps) => {
    return (
        <div>
            {
                props.documents.map((d, i) => {
                    return (
                        <div key={i}>{d.title}</div>
                    );
                })
            }
        </div>
    );
}

// component to show the search results for the text based document search
export const DocumentListItem = (props: IListItemProps<RailDocument>) =>
    <div className="list-item" onClick={() => props.toggle(props.item)}>
        <Checkbox checkedValue={props.selected === true} />
        <span className="document-type">{props.item.type}</span>
        {props.item.subType && <span className="document-sub-type">{props.item.subType}</span>}
        {props.item.title}
    </div>;

interface IRelatedDocumentsProps {
    isOpen: boolean,
    closeAction: () => void,
    document: Document
}

export const RelatedDocuments = (props: IRelatedDocumentsProps) => {
    const [selectedDocuments, setSelectedDocuments] = useState([] as RailDocument[]);
    const [menuDocuments, setMenuDocuments] = useState([] as ILeafItem[]);
    const [activeFilter, setActiveFilter] = useState(null as IDocumentFilter | null);
    const [activeSubFilter, setActiveSubFilter] = useState(null as IDocumentFilter | null);
    const [documentsByNameFilter, setDocumentsByNameFilter] = useState("");
    const [documentsByNameExpanded, setDocumentsByNameExpanded] = useState(false);
    const [documentSearchSummary, setDocumentSearchSummary] = useState("");
    const [documentModule, setDocumentModule] = useState(null as IDocumentModule |null);

    useEffect(() => {
        if (!props.isOpen) return;

        const currentMode = getUserExistingRulebookMode();
        var rulebookCache = RulebookNameToEnum(currentMode) === RulebookModeEnum.Traditional ? RelatedRulebookDocumentViewTraditional.cache :
            RulebookNameToEnum(currentMode) === RulebookModeEnum.CBTC ? RelatedRulebookDocumentViewCBTC.cache : undefined;
        setDocumentModule(RulebookNameToEnum(currentMode) === RulebookModeEnum.Traditional ? RelatedRulebookDocumentViewTraditional :
            RulebookNameToEnum(currentMode) === RulebookModeEnum.CBTC ? RelatedRulebookDocumentViewCBTC : RelatedRulebookDocumentViewTraditional);

        var relatedDocsIds = (props?.document?.relatedDocuments ?? []).map(rd => rd.id);

        if (rulebookCache && relatedDocsIds.length > 0) {
            // is the cache stale?
            if (rulebookCache.lastRefresh.getTime() + rulebookCache.maxAge < new Date().getTime()) {
                // update the cache and use that list
                rulebookCache.refresh().then(result => {
                    rulebookCache = result;

                    if (relatedDocsIds.length > 0) {
                        setSelectedDocuments(rulebookCache.data.filter(doc => relatedDocsIds.includes(doc.id)));
                    }
                });
            } else {
                // use the cache
                setSelectedDocuments(rulebookCache.data.filter(doc => relatedDocsIds.includes(doc.id)));
            }
        }
    }, [props.isOpen]);

    const [isConfirmClose, setIsConfirmClose] = useState(false);

    const confirmCloseText: string = 'Are you sure you want to exit?\nUnsaved data will be lost.';

    const showClosePrompt = () => {
        setIsConfirmClose(true);
    }

    const hideClosePrompt = () => {
        setIsConfirmClose(false);
    }

    const closeModal = () => {
        setIsConfirmClose(false);
        setMenuDocuments(f => f.filter(x => false));
        props.closeAction()
    }

    // Adds releated documents to the document object
    const addDocuments = () => {
        const doc = props.document;
        doc.relatedDocuments = selectedDocuments;
        documentAddEditState.setState(s => s.document = doc);
        closeModal();
    }

    // removes documents that have been selected o
    const removeDocument = (id: number) => {
        setSelectedDocuments(f => f.filter(i => i.id != id));
        const leafItem = menuDocuments.filter(x => x.id === id)[0];
        if (leafItem && leafItem.selected) leafItem.selected = false;
    }

    const selectDocument = (relatedDocument: RailDocument) => {
        setSelectedDocuments(f => f.filter(i => i.id !== relatedDocument.id).concat(relatedDocument));
        const leafItem = menuDocuments.filter(x => x.id === relatedDocument.id)[0];
        if (leafItem && !leafItem.selectAction) leafItem.selected = true;
    }

    const onDocumentClick = (doc: RailDocument) => {
        if (selectedDocuments.findIndex(sd => sd.id === doc.id) > -1) {
            removeDocument(doc.id);
        } else {
            selectDocument(doc);
        }
    };

        const documentSearchByName = async (name: string, documents?: RailDocument[]) => {
       
        const currentMode = getUserExistingRulebookMode();
        var rulebookCache = RulebookNameToEnum(currentMode) === RulebookModeEnum.Traditional ? RelatedRulebookDocumentViewTraditional.cache :
            RulebookNameToEnum(currentMode) === RulebookModeEnum.CBTC ? RelatedRulebookDocumentViewCBTC.cache : undefined;

        if (!documents || !rulebookCache?.data) {
            return [] as RailDocument[];
            }

        const lcName = name.toLowerCase();

        const result = await documents.filter(doc => {
            return (doc.title.toLowerCase().indexOf(lcName) > -1
                || doc.documentNumber.toLowerCase().indexOf(lcName) > -1
                || doc.type?.toLowerCase().indexOf(lcName) > -1
                || doc.subType?.toLowerCase().indexOf(lcName) > -1
                || doc.fileName.toLowerCase().indexOf(lcName) > -1);
        });

        setDocumentSearchSummary(result.length + ' search result' + (result.length === 1 ? "" : "s") + ' for the search "' + name + '"');

        return result;
    };

    return (
        <div className="related-document-modal">
            <Modal
                isOpen={props.isOpen}
                className="related-document-modal">
                <ModalHeader>
                    <RelatedDocumentsH2 />
                    <ModalClose onClick={showClosePrompt} />
                </ModalHeader>
                <ModalBody className="">

                    <Row className="sub-heading-text"><h4>Selected documents:</h4></Row>
                    <Row className="selected-documents-chip-container"><RelatedDocumentChips documents={selectedDocuments} deleteAction={removeDocument} /></Row>

                    <Row className="sub-heading-text"><h4>Search for related documents</h4></Row>
                    <Row className="no-gutters">
                        <Col xs={12} className="float-left">
                            <List<RailDocument>
                                listItemType={DocumentListItem}
                                filterText={documentsByNameFilter}
                                setFilterText={setDocumentsByNameFilter}
                                expanded={documentsByNameExpanded}
                                setExpanded={setDocumentsByNameExpanded}
                                buildKey={item => { return item.id; }}
                                dataSource={documentModule?.cache?.data}
                                searchDelayMs={1}
                                displayLoadingData={true}
                                refreshFilterOnDataSourceChange={true}
                                filterAsync={documentSearchByName}
                                summary={documentSearchSummary}
                                toggleSelected={onDocumentClick}
                                noResultsMessage="No documents found"
                                filterTextPlaceholder="Enter document title, document number or keyword"
                                selected={selectedDocuments} />
                        </Col>
                    </Row>

                    <Row className="sub-heading-text"><h4>Or select documents from the list</h4></Row>
                    <Row xs="1">
                        <DocumentFilterList
                            view={documentModule}
                            selectedDocuments={selectedDocuments}
                            onDocumentClick={onDocumentClick}
                            hideSubFilters={false}
                            activeFilter={activeFilter}
                            setActiveFilter={setActiveFilter}
                            activeSubFilter={activeSubFilter}
                            setActiveSubFilter={setActiveSubFilter} />
                    </Row>
                    <div className="modal-buton-container cols-12">
                        <Button className="modal-button btn-primary" onClick={addDocuments}> Add documents</Button>
                    </div>
                </ModalBody>
            </Modal>

            <ExitCancelModel
                text={confirmCloseText}
                actionButtonText={'Exit'}
                closeButtonText={'Cancel'}
                isOpen={isConfirmClose}
                action={closeModal}
                closeAction={hideClosePrompt} />
        </div>
    );
};

export default RelatedDocuments;